import React, { useState, useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const COLOR = {
  operativo:   { fill: '#0f6e56', stroke: '#0a4f3e', label: 'Operativo' },
  observacion: { fill: '#e65100', stroke: '#b34100', label: 'En observación' },
  detenido:    { fill: '#c62828', stroke: '#8e1a1a', label: 'Detenido' },
};

const ESTADO_LABEL = {
  operativo:   { bg: '#e8f5f1', color: '#0f6e56', text: 'Operativo' },
  observacion: { bg: '#fff3e0', color: '#e65100', text: 'En observación' },
  detenido:    { bg: '#ffebee', color: '#c62828', text: 'Detenido / Fuera de servicio' },
};

function estadoAscensor(e) {
  if (!e) return 'operativo';
  const v = e.toLowerCase();
  if (v === 'operativo') return 'operativo';
  if (v.includes('deten') || v.includes('servicio')) return 'detenido';
  if (v.includes('observa')) return 'observacion';
  return 'operativo';
}

export default function MapaComunidades({ comunidades }) {
  const [seleccionada, setSeleccionada] = useState(null);

  // Comunidades con coordenadas para el mapa
  const conCoordenadas = useMemo(
    () => comunidades.filter(c => c.lat != null && c.lng != null),
    [comunidades]
  );

  // Bounds para ajustar la vista inicial a todos los puntos
  const bounds = useMemo(() => {
    if (!conCoordenadas.length) return null;
    return conCoordenadas.map(c => [c.lat, c.lng]);
  }, [conCoordenadas]);

  const sinCoordenadas = comunidades.length - conCoordenadas.length;

  return (
    <div>
      {/* Leyenda */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
        {Object.entries(COLOR).map(([k, v]) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              display: 'inline-block', width: 14, height: 14, borderRadius: '50%',
              background: v.fill, border: `2px solid ${v.stroke}`,
            }} />
            <span style={{ fontSize: 12, color: '#555' }}>{v.label}</span>
          </div>
        ))}
        {sinCoordenadas > 0 && (
          <span style={{ fontSize: 11, color: '#aaa' }}>
            · {sinCoordenadas} comunidad{sinCoordenadas > 1 ? 'es' : ''} sin coordenadas configuradas
          </span>
        )}
      </div>

      {/* Mapa */}
      <MapContainer
        bounds={bounds || [[-33.65, -70.85], [-33.25, -70.45]]}
        boundsOptions={{ padding: [40, 40] }}
        style={{ height: 260, borderRadius: 12, border: '1px solid #e0e0e0' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {conCoordenadas.map(c => {
          const col = COLOR[c.estado] || COLOR.operativo;
          return (
            <CircleMarker
              key={c.comunidad}
              center={[c.lat, c.lng]}
              radius={10}
              pathOptions={{
                fillColor: col.fill,
                fillOpacity: 0.85,
                color: col.stroke,
                weight: 2,
              }}
              eventHandlers={{ click: () => setSeleccionada(c) }}
            >
              <Tooltip direction="top" offset={[0, -8]} opacity={0.95}>
                <div style={{ fontSize: 12 }}>
                  <strong>{c.comunidad}</strong><br />
                  {c.n_ascensores} ascensor{c.n_ascensores !== 1 ? 'es' : ''} ·{' '}
                  <span style={{ color: col.fill, fontWeight: 600 }}>
                    {col.label}
                  </span>
                </div>
              </Tooltip>
            </CircleMarker>
          );
        })}
      </MapContainer>

      {/* Panel de detalle al hacer clic en una comunidad */}
      {seleccionada && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: 16,
        }}
          onClick={e => { if (e.target === e.currentTarget) setSeleccionada(null); }}
        >
          <div style={{
            background: 'white', borderRadius: 16, padding: 0,
            width: '100%', maxWidth: 560, maxHeight: '80vh',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              background: '#0f6e56', padding: '16px 20px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ color: 'white', fontWeight: 700, fontSize: 16 }}>
                  {seleccionada.comunidad}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, marginTop: 2 }}>
                  {seleccionada.n_ascensores} ascensor{seleccionada.n_ascensores !== 1 ? 'es' : ''}
                </div>
              </div>
              <button onClick={() => setSeleccionada(null)}
                style={{
                  background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8,
                  color: 'white', fontSize: 18, cursor: 'pointer',
                  width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                ×
              </button>
            </div>

            {/* Lista de ascensores */}
            <div style={{ overflowY: 'auto', padding: 16, flex: 1 }}>
              {seleccionada.ascensores.map((a, i) => {
                const est = estadoAscensor(a.estado_final_equipo);
                const estStyle = ESTADO_LABEL[est];
                return (
                  <div key={a.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 14px', borderRadius: 10, marginBottom: 8,
                    background: i % 2 === 0 ? '#f9f9f9' : 'white',
                    border: '1px solid #efefef',
                  }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>
                        {a.label}
                      </div>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                        {a.codigo} · N° {a.n_ascensor}
                        {a.torre ? ` · Torre ${a.torre}` : ''}
                      </div>
                    </div>
                    <span style={{
                      ...estStyle, borderRadius: 20,
                      padding: '4px 12px', fontSize: 11, fontWeight: 700,
                      whiteSpace: 'nowrap', marginLeft: 10,
                    }}>
                      {estStyle.text}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
